<template>
    <wrapper-features-templates-operations
    :hideWhiteBoxOnLoad="showLoader || (!showLoader && showAccountsEmptyState && !applyErrorState)"
    :showOverlayLoader="showOverlayLoader"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
    :showStepper="(activeStep > 0 && activeStep < 3) && merchantSelected"
    :stepperParams="stepperParams"
    :activeStep="activeStep"
    :showFooter="activeStep < 3 && (tabsList[1].selected || merchantSelected)"
    :buttonFullWidth="activeStep === 0"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenTitleAppend="t('payments.BAIPaga.tokenTitle')"
    :feedbackOperationData="feedbackOperationData"
    :scrollValue="scrollValue"
    :customMobileBackIcon="merchantSelected ? 'close' : null"
    @challengeSuccessHandler="paymentSuccessHandler"
    @challengeErrorHandler="errorHandler($event, baiPagaMerchantPayment)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <component
        v-if="!showLoader && !applyErrorState && !showAccountsEmptyState"
        :is="getActiveStepComponent"
        :tabsList="tabsList"
        :formData="formData"
        :showInputsErrors="showInputsErrors"
        :responseData="responseData"
        :showFavoriteButton="showFavoriteButton"
        :isFavorite="isFavorite"
        @updateMerchantSelectorTabs="tabClickedHandler"
        @updateFormData="updateFormDataHandler"
        @validateMerchantId="validateMerchantName"
        @requestMerchantName="requestMerchantName"
        @changeMerchant="changeMerchantHandler"
        @resetShowInputsErrors="showInputsErrors = false"
        @showFavoriteModal="showFavoritesModal = true"
        @newPayment="newPaymentHandler"
        @scrollTo="scrollValue = $event"
        @backClicked="emit('backOrCloseClicked')"/>
        <template #empty-state-slot>
            <!-- Empty State -->
            <empty-state-generic
            v-if="showAccountsEmptyState && !applyErrorState"
            illustration="definitions-accounts-groups"
            :title="t('payments.BAIPaga.noAccountsTitle')"
            :description="t('payments.BAIPaga.noAccountsText')"/>
        </template>
    </wrapper-features-templates-operations>
    <!-- Favorites Modal for Step3 -->
    <favorites-modal
    v-if="showFavoritesModal"
    :modalParams="{ operationId: responseData.operationId, type: isFavorite ? 'remove' : 'create' }"
    @hideMe="showFavoritesModal = false"
    @requestSuccessful="favoriteModalSuccessHandler($event)"
    @requestError="favoriteModalErrorHandler($event)"/>
    <!-- Feedback Toasts -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    :triggerClose="triggerClose"
    @hideMe="[showFeedbackToast = false, triggerClose = false]"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import FavoritesModal from '@/components/generic/modals/centered/Favorite'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { emptyObjectConstructor, errorObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* props received
@params { Object } - data received from the reused payment
*/
const props = defineProps({
    params: Object
})

/* mapActions & mapGetters */
const { requestAccountsBalances, getMerchantDetail, getMerchantName, payToBaiPagaMerchant } = mapActions(moduleMapper.PAYMENTS_BAI_PAGA)
const { getAccountsBalances } = mapGetters(moduleMapper.PAYMENTS_BAI_PAGA)

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const merchantSelected = ref(false)
const scrollValue = ref(0)
const showAccountsEmptyState = computed(() => {	return !showLoader.value && !getAccountsBalances.value.length })
const merchantName = ref(null)

/*************************************************
*                                                *
*                   Tabs Data                    *
*                                                *
*************************************************/
const tabsList = ref([
	{
        id: 'code',
        label: t('payments.BAIPaga.tabs.code'),
        selected: true
    },
	{
        id: 'manual',
        label: t('payments.BAIPaga.tabs.manual'),
        selected: false
    }
])

const tabClickedHandler = (event) => {
    tabsList.value.forEach(tab => {
        tab.selected = tab.id === event
    })

    if (event === 'code') formData.value.amount = ''
}

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(0)

const stepperParams = ref([
    { description: t('payments.BAIPaga.stepper.data') },
    { description: t('payments.BAIPaga.stepper.validation') }
])

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
    if (showFeedbackToast.value) triggerClose.value = true
}

const getActiveStepComponent = computed(() => {
    if (!merchantSelected.value) {
        return defineAsyncComponent(() => import('@/components/features/payments/BAIPaga/MerchantSelector'))
    }

    const stepNumber = activeStep.value

	return defineAsyncComponent(() => import('@/components/features/payments/BAIPaga/Step' + stepNumber))
})

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const mandatoryList = ref([
    /* Merchant Name */
    'merchantName',
    /* Account Data */
    'originAccount',
    /* Amount Data */
    'amount'
])

const showInputsErrors = ref(false)

const formData = ref({
    merchantName: '',
    merchantId: '',
    originAccount: '',
    currency: '',
    amount: '',
    merchantExternalId: ''
})

const updateFormDataHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
}

const changeMerchantHandler = () => {
    tabClickedHandler('code')
    clearInputs()
    merchantSelected.value = false
    goToStep(0)
}

const reusePaymentDataHandler = () => {
    formData.value.merchantExternalId = props.params.merchant.externalId
    formData.value.merchantName = props.params.merchant.friendlyName
    formData.value.amount = props.params.amount
    requestMerchantName()
}
/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const clearInputs = () => {
    const tempId = formData.value.merchantExternalId

    clearObjectStrings(formData.value)

    formData.value = {
        ...formData.value,
        originAccount: getAccountsBalances.value.find(account => account.default).id,
        currency: getAccountsBalances.value.find(account => account.default).currency,
        merchantExternalId: tempId,
        merchantName: merchantName.value
    }
}

const validateInputs = () => {
    const errorState = atLeastOneIsEmpty(formData.value, mandatoryList.value) ||
    (formData.value.amount && formData.value.amount.length && parseInt(formData.value.amount) === 0)

    errorState ? showInputsErrors.value = true : goToStep(2)
}

/*************************************************
*                                                *
*                   Requests                     *
*                                                *
*************************************************/
const accountsBalancesRequest = async () => {
    cancelAllPendingRequests()
    showLoader.value = true
    applyErrorState.value = false

    try {
        await requestAccountsBalances()
        if (getAccountsBalances.value.length) {
            /* Setting Initial Selected Account */
            formData.value.originAccount = getAccountsBalances.value.find(account => account.default).id
            /* Setting Initial Currency from Default Account */
            formData.value.currency = getAccountsBalances.value.find(account => account.default).currency
        }

        if (props.params) {
            reusePaymentDataHandler()
            return
        }

        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, accountsBalancesRequest)
    }
}

const requestMerchantName = async () => {
    cancelAllPendingRequests()

    merchantSelected.value = false
	showLoader.value = true

    const payload = {
        merchantExternalId: formData.value.merchantExternalId
    }

	try {
        const res = await getMerchantName(payload)
        formData.value.merchantName = res.data.data.friendlyName
        formData.value.merchantExternalId = res.data.data.externalId
        merchantSelected.value = true

        showLoader.value = false

        goToStep(1)

        /* To force QRScanner close */
        if (window.cordova && window.QRScanner) {
            window.QRScanner.destroy()
        }
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestMerchantName)
	}
}

const validateMerchantName = async () => {
    cancelAllPendingRequests()

    if (!formData.value.merchantName) {
        showInputsErrors.value = true
        return
    }

    merchantSelected.value = false
	showOverlayLoader.value = true

    const payload = {
        merchantFriendlyName: formData.value.merchantName
    }

	try {
        const res = await getMerchantDetail(payload)
        merchantName.value = res.data.data.friendlyName
        formData.value.merchantName = merchantName.value
        formData.value.merchantExternalId = res.data.data.externalId
        merchantSelected.value = true

		showOverlayLoader.value = false

        /* To force QRScanner close */
        if (window.cordova && window.QRScanner) {
            window.QRScanner.destroy()
        }
        goToStep(1)
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, validateMerchantName)
	}
}

const baiPagaMerchantPayment = async () => {
    if (showFeedbackToast.value) triggerClose.value = true
	showOverlayLoader.value = true

    const payload = {
        amount: formData.value.amount,
        originAccountId: formData.value.originAccount,
        currency: formData.value.currency,
        merchantExternalId: formData.value.merchantExternalId
    }

	try {
        await payToBaiPagaMerchant(payload)
        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, payToBaiPagaMerchant)
	}
}

const newPaymentHandler = () => {
    tabClickedHandler('code')
    clearInputs()
    merchantSelected.value = false
    feedbackOperationData.value = null
    goToStep(0)
}

/*************************************************
*                                                *
*                Operation Status                *
*                                                *
*************************************************/
const feedbackOperationData = ref(null)
const responseData = ref()

/*************************************************
*                                                *
*                 Favorite Modal                 *
*                                                *
*************************************************/
const showFavoritesModal = ref(false)
const isFavorite = ref(false)
const showFavoriteButton = ref(false)

const favoriteModalSuccessHandler = $event => {
    showFavoritesModal.value = false
    isFavorite.value = !isFavorite.value
    showToastHandler($event)
}

const favoriteModalErrorHandler = $event => {
    showToastHandler($event, 'error')
    showFavoritesModal.value = false
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const triggerClose = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())
const applyErrorState = ref(false)

const showToastHandler = (data, status) => {
    feedbackToastData.value = {
        text: data.message || t('genericErrorMessage'),
        toastType: data.errorLevel && !status ? data.errorLevel.toLowerCase() : status
    }
    showFeedbackToast.value = true
}

const paymentSuccessHandler = data => {
    responseData.value = data.data
    showFavoriteButton.value = data.data.favoritable
    isFavorite.value = !data.data.favoritable

    responseData.value = {
        ...responseData.value,
        errorLevel: data.errorLevel,
        code: data.code
    }

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !responseData.value.mobilePaymentView?.hasReceipt ? null : {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: responseData.value.operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: responseData.value.operationId } }),
            operationId: responseData.value.operationId
        }
    }

    goToStep(3)
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*                     Footer                     *
*                                                *
*************************************************/

const footerButtonsParams = ref([
    {
        label: t('payments.BAIPaga.footerButtons.forward'),
        buttonType: 'primary',
        buttonPosition: 'left',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        step: 0,
        callbackFunction: validateMerchantName
    },
    {
        label: t('payments.BAIPaga.footerButtons.clear'),
        buttonPosition: 'left',
        step: 1,
        callbackFunction: clearInputs
    }, {
        label: t('payments.BAIPaga.footerButtons.forward'),
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        step: 1,
        callbackFunction: validateInputs
    }, {
        label: t('payments.BAIPaga.footerButtons.back'),
        buttonPosition: 'right',
        icon: 'arrow-left-full',
        step: 2,
        callbackFunction: () => { goToStep(1) }
    }, {
        label: t('payments.BAIPaga.footerButtons.validate'),
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'check-line',
        iconPosition: 'right',
        step: 2,
        callbackFunction: baiPagaMerchantPayment
    }
])

/* Initial Request */
accountsBalancesRequest()
</script>

<style lang="scss" scoped>
</style>
